import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Whether the line this selector belongs to has a server-locked quantity
   * (free sample, gift). The same Liquid condition renders the
   * `cart-items__quantity--locked` wrapper class and the `disabled` attributes.
   * @returns {boolean} True when the quantity must not be changed client-side
   */
  isQuantityLocked() {
    return this.closest('.cart-items__quantity--locked') != null;
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are managed client-side, except on server-locked lines: those
   * stay disabled so keyboard and scripted clicks cannot bypass the lock.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const locked = this.isQuantityLocked();

    this.#setButtonState(minusButton, locked || this.serverDisabledMinus || value <= min);
    this.#setButtonState(
      plusButton,
      locked || this.serverDisabledPlus || (effectiveMax !== null && value >= effectiveMax)
    );
  }

  /**
   * Applies a disabled state to a quantity button, keeping ARIA in sync
   * @param {HTMLButtonElement} button - The button to update
   * @param {boolean} disabled - Whether the button must be disabled
   */
  #setButtonState(button, disabled) {
    button.disabled = disabled;

    if (disabled) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
